import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl } from '@angular/forms';

/**
 * Componente riutilizzabile per visualizzare errori di validazione
 * 
 * Usage:
 * <app-validation-error 
 *   [control]="form.get('fieldName')" 
 *   errorId="fieldName-error"
 *   mode="strict"
 * />
 * 
 * Modes:
 * - strict: Blocca < > " ' & / \ (default)
 * - name: Solo lettere permesse
 * - alphanumeric: Solo lettere e numeri
 * - address: Permette lettere, numeri, spazi e punteggiatura per indirizzi
 * - description: Permette punteggiatura completa per descrizioni
 */
@Component({
  selector: 'app-validation-error',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (control?.invalid && control?.touched) {
      <small [id]="errorId" class="text-red-500 block mt-1" role="alert">
        @if (control?.hasError('required')) {
          Campo obbligatorio
        } @else if (control?.hasError('specialChars')) {
          @if (mode === 'name') {
            ⚠️ Sono permesse solo lettere, spazi, apostrofi, trattini e accenti
          } @else if (mode === 'alphanumeric') {
            ⚠️ Sono permessi solo lettere e numeri
          } @else if (mode === 'address') {
            ⚠️ Sono permessi lettere, numeri, spazi, virgole, punti, trattini, apostrofi e barre
          } @else if (mode === 'free-text') {
            ⚠️ Il testo non può contenere tag HTML o script
          } @else if (mode === 'description') {
            ⚠️ Caratteri speciali non permessi (< > " ' & / \)
          } @else {
            ⚠️ Caratteri speciali non permessi (< > " ' & / \)
          }
        } @else if (control?.hasError('cityCountryFormat')) {
          Rispettare il formato: Città, Nazione
        } @else if (control?.hasError('email')) {
          Formato email non valido
        } @else if (control?.hasError('invalidVatNumber')) {
          {{ vatNumberMessage }}
        } @else if (control?.hasError('invalidVatCountry')) {
          Seleziona un paese IVA valido
        } @else if (control?.hasError('vatCountriesUnavailable')) {
          Paesi IVA non disponibili. Ricarica la pagina prima di salvare
        } @else if (control?.hasError('invalidTaxCode')) {
          Codice Fiscale non valido. Deve contenere 16 caratteri alfanumerici o 11 cifre numeriche
        } @else if (control?.hasError('invalidPecEmail')) {
          Formato email PEC non valido
        } @else if (control?.hasError('invalidPhoneNumber')) {
          Numero di telefono non valido. Formato: +39 seguito da 8-11 cifre o 8-11 cifre
        } @else if (control?.hasError('invalidMobileNumber')) {
          Numero di cellulare non valido. Formato: +39 seguito da 3 e altre 9 cifre, oppure numero che inizia con 3 seguito da 9 cifre
        } @else if (customMessage) {
          {{ customMessage }}
        }
      </small>
    }
  `,
  styles: []
})
export class ValidationErrorComponent {
  /**
   * Il FormControl o AbstractControl da validare
   */
  @Input() control: AbstractControl | null = null;

  /**
   * ID univoco per l'elemento small (per aria-describedby)
   */
  @Input() errorId: string = '';

  /**
   * Modalità di validazione per messaggi specifici
   */
  @Input() mode: 'strict' | 'name' | 'alphanumeric' | 'address' | 'description' | 'free-text' = 'strict';

  /**
   * Messaggio custom opzionale per errori non standard
   */
  @Input() customMessage?: string;
  @Input() vatNumberMessage = 'Partita IVA non valida. Deve contenere esattamente 11 cifre numeriche';
}
